import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { useToast } from '../components/context/ToastContext';
import 'react-phone-number-input/style.css';
import { trackOnboardingCompleted, updateIntercom } from '../lib/intercom';
import { phOnboardingCompleted } from '../lib/posthog';
import { requestFeedback } from '../lib/feedback';
import { getStoredAttribution } from '../lib/attribution';
import AuthStepper from '../components/AuthStepper';
import '@styles/pages/onboarding.css';

/* ───────────────────────────────────────────
   REFERRAL OPTIONS
   ─────────────────────────────────────────── */

const REFERRAL_OPTIONS = [
  { icon: '📱', label: 'Social Media' },
  { icon: '👥', label: 'Friend / Family' },
  { icon: '🔍', label: 'Google Search' },
  { icon: '🤖', label: 'AI Assistant' },
  { icon: '📰', label: 'News / Article' },
  { icon: '🎙️', label: 'Podcast' },
  { icon: '💬', label: 'Other' },
] as const;

// Stable outside component so it doesn't need to be a useCallback dependency
const nameRegex = /^[a-zA-ZÀ-ÿ' -]+$/;

/* ═══════════════════════════════════════════
   COMPONENT
   Single-page onboarding, collapsed from the 5-step wizard.
   All field state, sessionStorage persistence, validation, and the
   submit handler are unchanged. Only the render and navigation
   logic are new: every field is visible at once, one CTA at the bottom.
   ═══════════════════════════════════════════ */

export default function Onboarding() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  /* ── state ── */
  const [fullName, setFullName] = useState(() => sessionStorage.getItem('ob_fullName') ?? '');
  const [dob, setDob] = useState(() => sessionStorage.getItem('ob_dob') ?? '');
  const [phone, setPhone] = useState<string | undefined>(() => sessionStorage.getItem('ob_phone') ?? undefined);
  // SMS consent, defaults UNCHECKED per TCPA. Bundling with terms is not
  // sufficient consent; this must be a deliberate user action.
  const [smsConsent, setSmsConsent] = useState<boolean>(() => sessionStorage.getItem('ob_smsConsent') === 'true');
  // SMS terms disclosure, collapsed by default, user expands inline. The
  // disclosure stays on the same page so it's still "clear and
  // conspicuous" pre-opt-in, just not visually dominant.
  const [showSmsTerms, setShowSmsTerms] = useState<boolean>(false);
  const [referral, setReferral] = useState(() => sessionStorage.getItem('ob_referral') ?? '');
  const [referralOther, setReferralOther] = useState(() => sessionStorage.getItem('ob_referralOther') ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-field "touched" tracking so validation messages only surface after
  // the user leaves a field — not while they're still mid-typing.
  const [touched, setTouched] = useState<{ fullName?: boolean; dob?: boolean; phone?: boolean }>({});
  const markTouched = (field: 'fullName' | 'dob' | 'phone') =>
    setTouched((t) => ({ ...t, [field]: true }));

  // Phone validation is debounced: hide the message the moment the user
  // starts editing, then re-show it after a brief pause. Stops the "invalid"
  // flash on intermediate keystrokes while a valid number is being typed.
  const [showPhoneError, setShowPhoneError] = useState(false);
  const phoneErrorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Debounced phone feedback: while the user types we keep the message
     hidden and only re-check ~700ms after they pause; on blur we validate
     immediately. Avoids the invalid flash on intermediate keystrokes. */
  const handlePhoneChange = (val?: string) => {
    setPhone(val);
    if (phoneErrorTimer.current) clearTimeout(phoneErrorTimer.current);
    if (!touched.phone) return; // stay silent until the field's first blur
    setShowPhoneError(false);
    phoneErrorTimer.current = setTimeout(() => {
      setShowPhoneError(!(val && isValidPhoneNumber(val)));
    }, 700);
  };

  const handlePhoneBlur = () => {
    markTouched('phone');
    if (phoneErrorTimer.current) clearTimeout(phoneErrorTimer.current);
    setShowPhoneError(!(phone && isValidPhoneNumber(phone)));
  };

  /* input ref for initial auto-focus */
  const nameRef = useRef<HTMLInputElement>(null);

  /* ── persist to sessionStorage ── */
  useEffect(() => { sessionStorage.setItem('ob_fullName', fullName); }, [fullName]);
  useEffect(() => { sessionStorage.setItem('ob_dob', dob); }, [dob]);
  useEffect(() => { if (phone) sessionStorage.setItem('ob_phone', phone); }, [phone]);
  useEffect(() => { sessionStorage.setItem('ob_smsConsent', String(smsConsent)); }, [smsConsent]);
  useEffect(() => { sessionStorage.setItem('ob_referral', referral); }, [referral]);
  useEffect(() => { sessionStorage.setItem('ob_referralOther', referralOther); }, [referralOther]);

  /* ── auth guard ── */
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) navigate('/signup');
    });
  }, [navigate]);

  /* ── auto-focus the first empty field on mount ── */
  useEffect(() => {
    if (!fullName) {
      const t = setTimeout(() => nameRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
  }, [fullName]);

  /* ── name helpers ── */
  const splitName = (full: string) => {
    const parts = full.trim().split(/\s+/);
    const first = parts[0] || '';
    const last = parts.slice(1).join(' ') || '';
    return { first, last };
  };

  /* ── DOB helpers ── */
  const handleDobChange = (raw: string) => {
    let digits = raw.replace(/\D/g, '').slice(0, 8);
    if (digits.length === 0) { setDob(''); return; }

    if (digits.length >= 1) {
      const m1 = Number(digits[0]);
      if (m1 > 1) digits = '0' + digits[0] + digits.slice(1);
    }
    if (digits.length >= 2) {
      let mm = Number(digits.slice(0, 2));
      if (mm === 0) mm = 1;
      if (mm > 12) mm = 12;
      digits = String(mm).padStart(2, '0') + digits.slice(2);
    }
    if (digits.length >= 4) {
      const mm = Number(digits.slice(0, 2));
      let dd = Number(digits.slice(2, 4));
      const maxDay = mm === 2 ? 29 : [4, 6, 9, 11].includes(mm) ? 30 : 31;
      if (dd === 0) dd = 1;
      if (dd > maxDay) dd = maxDay;
      digits = digits.slice(0, 2) + String(dd).padStart(2, '0') + digits.slice(4);
    } else if (digits.length === 3) {
      const d1 = Number(digits[2]);
      if (d1 > 3) digits = digits.slice(0, 2) + '0' + digits[2] + digits.slice(3);
    }
    if (digits.length >= 8) {
      let yyyy = Number(digits.slice(4, 8));
      const thisYear = new Date().getFullYear();
      if (yyyy < 1900) yyyy = 1900;
      if (yyyy > thisYear) yyyy = thisYear;
      digits = digits.slice(0, 4) + String(yyyy);
    }
    digits = digits.slice(0, 8);

    let formatted = '';
    if (digits.length > 4) formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    else formatted = digits;
    setDob(formatted);
  };

  const parseDob = (display: string): string | null => {
    const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return null;
    const [, mm, dd, yyyy] = match;
    const date = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    if (isNaN(date.getTime())) return null;
    if (date.getMonth() + 1 !== Number(mm) || date.getDate() !== Number(dd)) return null;
    return `${yyyy}-${mm}-${dd}`;
  };

  const isAdult = (isoDate: string) => {
    const birth = new Date(isoDate);
    if (isNaN(birth.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age >= 18;
  };

  /* ── per-field validation (kept identical to the wizard version) ── */
  const isFieldValid = useCallback(
    (field: 'fullName' | 'dob' | 'phone' | 'referral'): boolean => {
      switch (field) {
        case 'fullName': {
          const { first, last } = splitName(fullName);
          return (
            first.length >= 2 &&
            nameRegex.test(first) &&
            last.length >= 2 &&
            nameRegex.test(last)
          );
        }
        case 'dob': {
          const iso = parseDob(dob);
          return !!iso && isAdult(iso);
        }
        case 'phone':
          return !!phone && isValidPhoneNumber(phone);
        case 'referral':
          return referral !== '' && (referral !== 'Other' || referralOther.trim().length > 0);
        default:
          return false;
      }
    },
    [fullName, dob, phone, referral, referralOther],
  );

  const allValid = useMemo(
    () =>
      isFieldValid('fullName') &&
      isFieldValid('dob') &&
      isFieldValid('phone') &&
      isFieldValid('referral'),
    [isFieldValid],
  );

  /* ── DOB inline error (only when user has finished a complete date) ── */
  const dobIso = useMemo(() => parseDob(dob), [dob]);
  const dobError = dobIso && !isAdult(dobIso);

  /* Clear any pending phone-validation timer on unmount. */
  useEffect(() => () => {
    if (phoneErrorTimer.current) clearTimeout(phoneErrorTimer.current);
  }, []);

  /* ── submit ── */
  const handleSubmit = async () => {
    if (loading || !allValid) return;
    setLoading(true);
    setError(null);

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (!user || authError) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    const { first, last } = splitName(fullName);

    // SMS consent fields, only set sms_consent_at when the user actually
    // ticked the box. We always stamp prompt_seen_at since we explicitly
    // showed the consent UI, so the post-onboarding modal won't re-prompt.
    const nowIso = new Date().toISOString();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;

    // Capture browser timezone for TCPA quiet-hours enforcement on SMS.
    // Falls back to America/New_York if the browser doesn't expose Intl
    // (very rare; older browsers, some embedded WebViews).
    let tz: string | null = null;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      tz = null;
    }

    // First-touch attribution: only write utm_*/initial_referrer/landing_page
    // if no value is already on the profile. Re-running onboarding (rare,
    // but possible if the user bounces back to /onboarding) must NOT
    // overwrite the campaign that originally acquired them.
    const { data: existing } = await supabase
      .from('profiles')
      .select('utm_source')
      .eq('id', user.id)
      .maybeSingle();

    const attribution = getStoredAttribution();
    const attributionFields =
      attribution && !existing?.utm_source
        ? {
            utm_source: attribution.utm_source ?? null,
            utm_medium: attribution.utm_medium ?? null,
            utm_campaign: attribution.utm_campaign ?? null,
            utm_content: attribution.utm_content ?? null,
            utm_term: attribution.utm_term ?? null,
            initial_referrer: attribution.initial_referrer ?? null,
            landing_page: attribution.landing_page ?? null,
          }
        : {};

    const { error: upsertError } = await supabase.from('profiles').upsert({
      id: user.id,
      first_name: first,
      last_name: last,
      date_of_birth: parseDob(dob)!,
      phone_e164: phone,
      referral_source:
        referral === 'Other'
          ? referralOther.trim() || null
          : referral === 'AI Assistant' && referralOther.trim()
            ? `AI: ${referralOther.trim()}`
            : referral || null,
      onboarding_completed: true,
      timezone: tz,
      sms_consent_prompt_seen_at: nowIso,
      ...(smsConsent && {
        sms_consent_at: nowIso,
        sms_consent_user_agent: userAgent,
      }),
      ...attributionFields,
    });

    if (upsertError) {
      setError(upsertError.message);
      setLoading(false);
      return;
    }

    // Clear the wizard-era sessionStorage keys (including the now-unused
    // ob_step, in case it's lingering from a previous deploy).
    ['ob_step','ob_fullName','ob_dob','ob_phone','ob_smsConsent','ob_referral','ob_referralOther']
      .forEach(k => sessionStorage.removeItem(k));
    showToast('Profile saved!');
    trackOnboardingCompleted();
    /* 2026-08-08: onboarding completion was Intercom-only. It is one of the
       two hard gates between signup and first bill upload (HIPAA is the
       other), so the signup → activation funnel had two invisible steps. */
    phOnboardingCompleted();
    /* Ask how it went, a few seconds after they land on the dashboard. */
    requestFeedback('onboarding');
    updateIntercom({
      name: `${first} ${last}`,
      phone: phone,
      onboarding_completed: true,
    });
    navigate('/');
  };

  const handleEnterSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && allValid) {
      e.preventDefault();
      handleSubmit();
    }
  };

  /* ═══════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════ */
  return (
    <div className="onboarding-container">
      {/* Header */}
      <header className="auth-header">
        <div className="logo" aria-label="Mediloop">
          <img
            src="/images/logos/tail-logo.png"
            alt="Mediloop"
            className="logo-img"
            width={36}
            height={36}
          />
        </div>
      </header>

      <main className="ob-card ob-card--single">
        <AuthStepper currentStep="onboarding" />

        <h2 className="ob-heading">
          Who's <span className="italic">on the case?</span>
        </h2>
        <p className="ob-hint ob-hint--top">
          We use this only to verify your identity with your provider. Nothing else.
        </p>

        {/* ── Name ── */}
        <div className="ob-section">
          <label className="ob-label" htmlFor="ob-name">Full name</label>
          <input
            id="ob-name"
            ref={nameRef}
            className="ob-input"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            onBlur={() => markTouched('fullName')}
            onKeyDown={handleEnterSubmit}
            placeholder="Your full name"
            autoComplete="name"
          />
          {touched.fullName && !isFieldValid('fullName') && (
            <p className="ob-field-error">Please enter your first and last name.</p>
          )}
        </div>

        {/* ── DOB ── */}
        <div className="ob-section">
          <label className="ob-label" htmlFor="ob-dob">Date of birth</label>
          <input
            id="ob-dob"
            className="ob-input"
            type="text"
            value={dob}
            onChange={(e) => handleDobChange(e.target.value)}
            onBlur={() => markTouched('dob')}
            onKeyDown={handleEnterSubmit}
            placeholder="MM/DD/YYYY"
            inputMode="numeric"
            autoComplete="bday"
          />
          {dobError ? (
            <p className="ob-field-error">You must be 18 or older to use mediloop.</p>
          ) : (
            touched.dob && !isFieldValid('dob') && (
              <p className="ob-field-error">
                {dob ? 'Please enter a valid date (MM/DD/YYYY).' : 'Date of birth is required.'}
              </p>
            )
          )}
        </div>

        {/* ── Phone + SMS consent ── */}
        <div className="ob-section">
          <label className="ob-label">Phone number</label>
          <div
            className="ob-phone-wrap"
            onKeyDown={handleEnterSubmit}
          >
            <PhoneInput
              defaultCountry="US"
              placeholder="(555) 000-0000"
              value={phone}
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
            />
          </div>
          {showPhoneError && (
            <p className="ob-field-error">
              {phone ? 'Please enter a valid phone number.' : 'Phone number is required.'}
            </p>
          )}

          {/* SMS consent, separate explicit opt-in (TCPA). NEVER pre-check.
              Required disclosures live in the "View terms" expander below
              so they remain pre-opt-in accessible without dominating the
              page. */}
          <div className="ob-sms-consent-wrapper">
            <label className="ob-sms-consent">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="ob-sms-consent-input"
              />
              <span className="ob-sms-consent-label">
                Text me when there&apos;s an update on my case
              </span>
            </label>
            <div className="ob-sms-consent-meta">
              <span>Optional</span>
              <span className="ob-sms-consent-meta-sep" aria-hidden="true">&middot;</span>
              <button
                type="button"
                className="ob-sms-consent-toggle"
                onClick={() => setShowSmsTerms((v) => !v)}
                aria-expanded={showSmsTerms}
                aria-controls="ob-sms-terms"
              >
                {showSmsTerms ? 'Hide terms' : 'View terms'}
              </button>
            </div>
            {showSmsTerms && (
              <div id="ob-sms-terms" className="ob-sms-consent-fine" role="region">
                Automated SMS from Mediloop. Msg &amp; data rates may apply. Reply <strong>STOP</strong> to opt out at any time. Optional, not required to use Mediloop.
              </div>
            )}
          </div>
        </div>

        {/* ── Referral ── */}
        <div className="ob-section">
          <label className="ob-label">How did you find us?</label>
          <div className="ob-ref-grid">
            {REFERRAL_OPTIONS.map((opt) => (
              <button
                key={opt.label}
                type="button"
                className={`ob-ref-card ${referral === opt.label ? 'selected' : ''}`}
                onClick={() => setReferral(opt.label)}
              >
                <span className="ref-icon">{opt.icon}</span>
                <span className="ref-label">{opt.label}</span>
              </button>
            ))}
          </div>
          {(referral === 'Other' || referral === 'AI Assistant') && (
            <input
              className="ob-input ob-input--other"
              type="text"
              value={referralOther}
              onChange={(e) => setReferralOther(e.target.value)}
              onKeyDown={handleEnterSubmit}
              placeholder={
                referral === 'AI Assistant'
                  ? 'Which one? ChatGPT, Gemini, Perplexity… (optional)'
                  : 'Tell us how you found us'
              }
              autoFocus
            />
          )}
          {touched.phone && !isFieldValid('referral') && (
            <p className="ob-field-error">Let us know how you found us.</p>
          )}
        </div>

        {/* ── Trust badge ── */}
        <div className="ob-trust">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>Your information is encrypted and HIPAA-protected</span>
        </div>

        {/* ── Submit ── */}
        <button
          className="ob-cta-btn"
          disabled={loading || !allValid}
          onClick={handleSubmit}
        >
          {loading ? 'Saving…' : 'Put Agent Loop On The Case →'}
        </button>

        {error && <p className="ob-error">{error}</p>}
      </main>
    </div>
  );
}
